import React, { useEffect, useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Modal, Button, Icon, Progress } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import { EnvironmentContext } from '../Context/EnvironmentContext';
import './DailyRewardModal.css';

const STREAK_REWARDS = [
    { day: 1, credits: 500, icon: '🎁' },
    { day: 2, credits: 600, icon: '🎁' },
    { day: 3, credits: 750, icon: '🎁' },
    { day: 4, credits: 900, icon: '✨' },
    { day: 5, credits: 1000, icon: '✨' },
    { day: 6, credits: 1200, icon: '💎' },
    { day: 7, credits: 1500, icon: '👑' },
];

const DailyRewardModal = ({ isOpen, onClose, rewardData }) => {
    const { t } = useTranslation();
    const env = useContext(EnvironmentContext);
    const [showReward, setShowReward] = useState(false);
    const [claimed, setClaimed] = useState(false);

    const { tokensAwarded = 1000, consecutiveDays = 1, isFirstTime = false } = rewardData || {};

    useEffect(() => {
        if (isOpen) {
            setShowReward(false);
            setClaimed(false);
            // Delay showing the reward for dramatic effect
            const timer = setTimeout(() => setShowReward(true), 500);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleClaim = () => {
        setClaimed(true);
        
        // Trigger confetti
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#FFD700', '#FFA500', '#FF6347', '#00CED1', '#9370DB'],
        });

        // Play sound if enabled (uncomment when sounds are available)
        // if (env.playMusicBool) {
        //     try {
        //         const audio = new Audio('/sounds/reward.mp3');
        //         audio.volume = 0.3;
        //         audio.play().catch(() => {});
        //     } catch (e) {}
        // }

        // Close after animation
        setTimeout(() => {
            onClose();
        }, 2000);
    };

    const currentDayIndex = Math.min(consecutiveDays, 7) - 1;

    return (
        <AnimatePresence>
            {isOpen && (
                <Modal
                    open={isOpen}
                    onClose={onClose}
                    size="small"
                    className="daily-reward-modal"
                    closeOnDimmerClick={false}
                >
                    <Modal.Content className="daily-reward-content">
                        {/* Header */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="reward-header"
                        >
                            <h2>{isFirstTime ? t('dailyReward.welcome') : t('dailyReward.title')}</h2>
                            {!isFirstTime && (
                                <p className="streak-text">
                                    🔥 {t('dailyReward.streak', { days: consecutiveDays })}
                                </p>
                            )}
                        </motion.div>

                        {/* Streak Progress */}
                        {!isFirstTime && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2 }}
                                className="streak-progress"
                            >
                                <div className="streak-days">
                                    {STREAK_REWARDS.map((reward, index) => (
                                        <div
                                            key={index}
                                            className={`streak-day ${index < consecutiveDays ? 'completed' : ''} ${index === currentDayIndex ? 'current' : ''}`}
                                        >
                                            <span className="day-icon">{reward.icon}</span>
                                            <span className="day-number">{t('dailyReward.day')} {reward.day}</span>
                                            <span className="day-credits">+{reward.credits}</span>
                                        </div>
                                    ))}
                                </div>
                                <Progress
                                    value={consecutiveDays}
                                    total={7}
                                    size="small"
                                    color="yellow"
                                    className="streak-bar"
                                />
                            </motion.div>
                        )}

                        {/* Reward Box */}
                        <AnimatePresence>
                            {showReward && (
                                <motion.div
                                    initial={{ scale: 0, rotate: -180 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    exit={{ scale: 0, rotate: 180 }}
                                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                                    className="reward-box"
                                >
                                    <motion.div
                                        animate={!claimed ? {
                                            boxShadow: [
                                                '0 0 20px rgba(255, 215, 0, 0.3)',
                                                '0 0 40px rgba(255, 215, 0, 0.6)',
                                                '0 0 20px rgba(255, 215, 0, 0.3)',
                                            ],
                                        } : {}}
                                        transition={{ repeat: Infinity, duration: 1.5 }}
                                        className="reward-glow"
                                    >
                                        <div className="reward-icon">
                                            {isFirstTime ? '🎉' : STREAK_REWARDS[currentDayIndex]?.icon || '🎁'}
                                        </div>
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.3 }}
                                            className="reward-amount"
                                        >
                                            <span className="credits-plus">+</span>
                                            <motion.span
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ delay: 0.5, type: 'spring' }}
                                                className="credits-number"
                                            >
                                                {tokensAwarded.toLocaleString()}
                                            </motion.span>
                                            <span className="credits-label">{t('Credits')}</span>
                                        </motion.div>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Messages count */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.7 }}
                            className="messages-info"
                        >
                            {t('dailyReward.messagesInfo', { count: Math.floor(tokensAwarded / 10) })}
                        </motion.p>

                        {/* Claim Button */}
                        {showReward && !claimed && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.8 }}
                            >
                                <Button
                                    primary
                                    size="large"
                                    onClick={handleClaim}
                                    className="claim-button"
                                >
                                    <Icon name="gift" />
                                    {t('dailyReward.claim')}
                                </Button>
                            </motion.div>
                        )}

                        {/* Claimed state */}
                        {claimed && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="claimed-state"
                            >
                                <Icon name="check circle" size="huge" color="green" />
                                <p>{t('dailyReward.claimed')}</p>
                            </motion.div>
                        )}

                        {/* Next reward info */}
                        {!isFirstTime && consecutiveDays < 7 && (
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1 }}
                                className="next-reward-info"
                            >
                                {t('dailyReward.nextReward', { 
                                    credits: STREAK_REWARDS[Math.min(consecutiveDays, 6)]?.credits || 1300 
                                })}
                            </motion.p>
                        )}
                    </Modal.Content>
                </Modal>
            )}
        </AnimatePresence>
    );
};

export default DailyRewardModal;
